<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-global-presence-section__header">
        <JssRichText class="c-global-presence-section__title" :field="fields?.title" tag="h2" />
        <RichText
          v-if="fields.description?.value"
          class="c-global-presence-section__description"
          :field="fields.description"
        />
      </div>
    </ContentGrid>

    <ContentGrid :desktop-default-grid="[3, 18]" :mobile-default-grid="[1, 14]">
      <div v-if="cards.length" class="c-global-presence-section__cards">
        <article v-for="card in cards" :key="card.id" class="c-global-presence-section__card">
          <div class="c-global-presence-section__media">
            <AdaptiveMedia
              v-if="hasCardMedia(card)"
              :field="getCardMediaField(card)"
              :ratio="1.78"
              :show-play-button="true"
              :autoplay="false"
              :loop="false"
              :controls="true"
              :has-controls="true"
              is-background
            />
          </div>
          <div class="c-global-presence-section__card-body">
            <JssText
              v-if="card.fields?.title?.value"
              class="c-global-presence-section__card-title"
              :field="card.fields.title"
              tag="h3"
            />
            <RichText
              v-if="card.fields?.description?.value"
              class="c-global-presence-section__card-description"
              :field="card.fields.description"
            />
            <a
              v-if="getCardLink(card).href"
              class="c-global-presence-section__card-link"
              :href="getCardLink(card).href"
              :target="getCardLink(card).target"
              :rel="getCardLink(card).target === '_blank' ? 'noopener noreferrer' : undefined"
              :aria-label="getCardLink(card).label"
            ></a>
          </div>
        </article>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import AdaptiveMedia from '@/elements/AdaptiveMedia.vue';
import utils from '@/utils';
import type {
  GlobalPresenceSectionCardFields,
  GlobalPresenceSectionFields,
} from '@/types/components/GlobalPresenceSection';
import type { ScItem } from '@/types/sitecore';

interface GlobalPresenceSectionProps {
  fields: GlobalPresenceSectionFields;
  rendering?: any;
}

const props = defineProps<GlobalPresenceSectionProps>();

const fields = computed(() => props.fields);
const rendering = computed(() => props.rendering);

const cards = computed(() => fields.value?.cards ?? []);
const backgroundSrc = computed(() => fields.value?.backgroundImage?.value?.src || '');

const rootClass = computed(() =>
  utils.overall.baseComClass(props.fields, 'c-global-presence-section')
);
const rootStyle = computed(() => {
  const result = utils.overall.baseComStyle(props.fields);
  return { backgroundImage: `url(${backgroundSrc.value})`, ...result };
});

const hasCardMedia = (card: ScItem<GlobalPresenceSectionCardFields>) => {
  const mediaFields = card.fields?.video?.fields;

  return !!(
    mediaFields?.desktopImage?.value?.src ||
    mediaFields?.mobileImage?.value?.src ||
    mediaFields?.desktopVideo?.value?.src ||
    mediaFields?.desktopVideo?.value?.href ||
    mediaFields?.mobileVideo?.value?.src ||
    mediaFields?.mobileVideo?.value?.href
  );
};
const getCardMediaField = (card: ScItem<GlobalPresenceSectionCardFields>) => card.fields?.video;

const getCardLink = (card: ScItem<GlobalPresenceSectionCardFields>) => {
  const value = card.fields?.link?.value || {};
  return {
    href: value.href || '',
    target: value.target,
    label: value.text || value.title || card.fields?.title?.value || 'View details',
  };
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-global-presence-section {
  position: relative;
  overflow: hidden;
  background-position: center;
  background-size: cover;
  padding: 40px 0;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(239, 246, 241, 0.6);
    pointer-events: none;
  }

  > .e-content-grid {
    position: relative;
    z-index: 1;
  }

  &__header {
    margin: 0 auto 34px;
    text-align: center;
  }

  &__title {
    margin-bottom: 16px;
  }

  &__description {
    color: rgba(0, 0, 0, 0.78);
    font-size: 14px;
    line-height: 1.45;

    p {
      margin: 0;
    }
  }

  &__cards {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
  }

  &__card {
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.72);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.6);
    box-shadow: 0 16px 40px rgba(20, 38, 26, 0.08);
    backdrop-filter: blur(2px);
  }

  &__media {
    margin: 24px 24px 0;
    overflow: hidden;
    min-height: 210px;
    border-radius: 10px;
    background: $color-light-mercury;

    .e-adaptive-media,
    .e-video-player,
    video {
      width: 100%;
      height: 100%;
      min-height: 210px;
    }
  }

  &__card-body {
    position: relative;
    padding: 22px 24px 30px;

    &:has(.c-global-presence-section__card-link) {
      cursor: pointer;
    }
  }

  &__card-title {
    margin: 0 0 14px;
    color: $black;
    font-size: 22px;
    line-height: 1.25;
    font-weight: 700;
  }

  &__card-description {
    color: rgba(0, 0, 0, 0.62);
    font-size: 14px;
    line-height: 1.45;

    p {
      margin: 0;
    }
  }

  &__card-link {
    position: absolute;
    inset: 0;
    z-index: 2;

    &:focus-visible {
      outline: 2px solid #688f6f;
      outline-offset: -4px;
    }
  }

  @include tablet {
    &__header {
      margin-bottom: 48px;
    }

    &__cards {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 28px;
    }

    &__media {
      min-height: 230px;

      .e-adaptive-media,
      .e-video-player,
      video {
        min-height: 230px;
      }
    }
  }
}
</style>
